import React, { useContext, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { Button, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlusCircle, faFileArrowDown } from "@fortawesome/free-solid-svg-icons";

// Contexto (Solo estilos)
import { DarkModeContext } from "../../context/DarkModeContext";

// Componentes Estructurales
import BienvenidoPanelAsesores from "../../components/asesoresComp/BienvenidoPanelAsesores";
import BienvenidoPanelAsesoresCelular from "../../components/asesoresComp/BienvenidoPanelAsesoresCelular";
import TituloPagina from "../../components/comunes/TituloPagina";
import Footer from "../../components/comunes/Footer";
import ScrollTo from "../../components/asesoresComp/ScrollTo";

// Componentes CRM
import CRMTablaAlertas from "../../components/asesoresComp/CRMTablaAlertas";
import CRMModalGestion from "../../components/asesoresComp/CRMModalGestion";
import CRMModalHistorial from "../../components/asesoresComp/CRMModalHistorial";

// IMPORTAMOS EL MODAL QUE ACABAMOS DE CREAR
import CRMModalCrear from "../../components/asesoresComp/CRMModalCrear";
import { getToken } from "../../utils/auth";


const CRMPrincipalView = () => {
    const { darkMode } = useContext(DarkModeContext);
    const navegacion = useNavigate();
    const token = getToken();

    // Estados de Datos
    const [alertas, setAlertas] = useState([]);
    const [acciones, setAcciones] = useState([]);
    const [cargandoTabla, setCargandoTabla] = useState(false);
    const [descargandoExcel, setDescargandoExcel] = useState(false);

    // Estados de Interfaz
    const [modalShow, setModalShow] = useState(false); // Para abrir/cerrar el modal de crear
    const [datosInicio, setDatosInicio] = useState(null);

    // Estados para Gestión e Historial
    const [showModalGestion, setShowModalGestion] = useState(false);
    const [showModalHistorial, setShowModalHistorial] = useState(false);
    const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
    const [historialActual, setHistorialActual] = useState([]);
    const [formGestion, setFormGestion] = useState({ idRegla: "", observacion: "" });

    // ========================================================================
    // CONSULTAS API (FETCH) DEFINIDAS EN LA VISTA
    // ========================================================================

    // 1. Cargar Alertas (Tabla)
    const cargarAlertas = useCallback(async () => {
        if (!token) return;
        setCargandoTabla(true);
        try {
            const response = await fetch(process.env.REACT_APP_API_CRM_MIS_ALERTAS, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ token }),
            });
            if (response.ok) {
                const data = await response.json();
                setAlertas(Array.isArray(data) ? data : []);
            }
        } catch (error) {
            console.error("Error cargando alertas:", error);
        } finally {
            setCargandoTabla(false);
        }
    }, [token]);

    // 2. Cargar Acciones (Botones)
    const cargarAcciones = useCallback(async () => {
        if (!token) return;
        try {
            const response = await fetch(process.env.REACT_APP_API_CRM_OBTENER_ACCIONES, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ token }),
            });
            if (response.ok) {
                setAcciones(await response.json());
            }
        } catch (error) { console.error(error); }
    }, [token]);

    // Ejecutar al inicio
    useEffect(() => {
        if (!token) {
            Swal.fire("Sesión Expirada", "Ingresa nuevamente", "warning").then(() => navegacion("/"));
            return;
        }
        cargarAlertas();
        cargarAcciones();
    }, [token, cargarAlertas, cargarAcciones, navegacion]);

    // ========================================================================
    // HANDLERS
    // ========================================================================

    const handleGestionarClick = (alerta) => {
        setClienteSeleccionado(alerta);
        setFormGestion({ idRegla: "", observacion: "" });
        setShowModalGestion(true);
    };

    const handleGuardarGestion = async () => {
        if (!formGestion.idRegla) return Swal.fire("Atención", "Selecciona una acción", "warning");
        // Aquí podrías agregar el fetch de registrar gestión si quisieras sacarlo del componente hijo también
    };

    const handleVerHistorialClick = async (alerta) => {
        setClienteSeleccionado(alerta);
        setHistorialActual([]);
        setShowModalHistorial(true);
        // Aquí podrías agregar el fetch de historial
    };

    const handleDescargarExcelBase = useCallback(async () => {
        if (!token) {
            Swal.fire("Sesión Expirada", "Ingresa nuevamente", "warning").then(() => navegacion("/"));
            return;
        }

        setDescargandoExcel(true);
        try {
            const response = await fetch(process.env.REACT_APP_API_CRM_EXPORTAR_EXCEL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ Token: token }),
            });

            if (response.status === 401 || response.status === 403) {
                Swal.fire("Sesión Expirada", "Ingresa nuevamente", "warning").then(() => navegacion("/"));
                return;
            }

            if (!response.ok) {
                let mensaje = "No se pudo generar el Excel.";
                try {
                    const data = await response.json();
                    mensaje = data?.mensaje || mensaje;
                } catch { }
                throw new Error(mensaje);
            }

            const blob = await response.blob();
            const disposition = response.headers.get("content-disposition") || "";
            let fileName = "CRM_Base.xlsx";
            const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i);
            if (match?.[1]) {
                fileName = match[1].replace(/['"]/g, "").trim();
            }

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error descargando Excel CRM:", error);
            Swal.fire("Error", error.message || "No se pudo descargar el Excel.", "error");
        } finally {
            setDescargandoExcel(false);
        }
    }, [token, navegacion]);

    const alertasOrdenadas = Array.isArray(alertas)
        ? [...alertas].sort((a, b) => {
            const fa = a?.fechaProximoContacto ? new Date(a.fechaProximoContacto).getTime() : Number.MAX_SAFE_INTEGER;
            const fb = b?.fechaProximoContacto ? new Date(b.fechaProximoContacto).getTime() : Number.MAX_SAFE_INTEGER;
            return fa - fb;
        })
        : [];

    // ========================================================================
    // RENDER
    // ========================================================================
    return (
        <div className="d-flex flex-column min-vh-100" translate="no">
            <ScrollTo />

            <div className="py-4 d-md-block d-none"><BienvenidoPanelAsesores datos={datosInicio} /></div>
            <div className="py-4 d-block d-md-none"><BienvenidoPanelAsesoresCelular datos={datosInicio} /></div>
            <div><TituloPagina title="Gestión Comercial (CRM)" /></div>

            <div className="container mt-4 mb-5">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className={`m-0 fw-bold ${darkMode ? "text-white" : "text-dark"}`}>
                        Mis Casos Activos ({alertas ? alertas.length : 0})
                    </h4>

                    <div className="d-flex align-items-center">
                        <Button
                            className="border-0 mx-1"
                            variant="outline-success"
                            onClick={handleDescargarExcelBase}
                            disabled={descargandoExcel}
                        >
                            {descargandoExcel ? (
                                <Spinner animation="border" size="sm" />
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faFileArrowDown} className="me-2" />
                                    <span className="lato-bold fs-18">Excel Base</span>
                                </>
                            )}
                        </Button>

                        {/* BOTÓN PARA ABRIR MODAL */}
                        <Button
                            className="btn-publicar border-0 mx-1"
                            style={{ backgroundColor: "#b4c400" }}
                            onClick={() => setModalShow(true)}
                        >
                            <FontAwesomeIcon icon={faPlusCircle} className="me-2" />
                            <span className="lato-bold fs-18">Nuevo Prospecto</span>
                        </Button>
                    </div>
                </div>

                {/* TABLA */}
                <div className={`card shadow-sm border-0 ${darkMode ? "bg-secondary" : "bg-white"}`}>
                    <div className="card-body p-0">
                        {cargandoTabla ? (
                            <div className="text-center py-5"><Spinner animation="border" /></div>
                        ) : (
                            <CRMTablaAlertas
                                listaAlertas={alertasOrdenadas}
                                onGestionarClick={handleGestionarClick}
                                onVerHistorialClick={handleVerHistorialClick}
                                onRecargarTabla={cargarAlertas}
                            />
                        )}
                    </div>
                </div>
            </div>

            <div className="py-4 mt-auto"><Footer /></div>

            {/* --- EL MODAL NUEVO (IMPORTADO) --- */}
            {/* Le pasamos la función cargarAlertas para que la ejecute al terminar de guardar */}
            <CRMModalCrear
                show={modalShow}
                onHide={() => setModalShow(false)}
                onRecargarTabla={cargarAlertas}
            />

            {/* OTROS MODALES */}
            <CRMModalGestion
                show={showModalGestion} onHide={() => setShowModalGestion(false)}
                cliente={clienteSeleccionado} acciones={acciones}
                formGestion={formGestion} setFormGestion={setFormGestion}
                onGuardar={handleGuardarGestion}
                onRecargarTabla={cargarAlertas}
            />
            <CRMModalHistorial
                show={showModalHistorial} onHide={() => setShowModalHistorial(false)}
                cliente={clienteSeleccionado} historial={historialActual}
            />
        </div>
    );
};

export default CRMPrincipalView;
